import { useCallback, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { CountUnreadVoicemailsResponse } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/phone_records_api.pb';
import { Tag } from '@weave/schema-gen-ts/dist/schemas/tag/shared/v1/models.pb';
import {
  VoicemailEventType,
  VoicemailUpdateEvent,
} from '@weave/schema-gen-ts/dist/shared/phone/v1/voicemail/message.pb';
import { isEqual } from 'lodash-es';
import { InfiniteData, UseInfiniteQueryResult, useQueryClient } from 'react-query';
import { PhoneCallsQueries } from '@frontend/api-phone-calls';
import { TagsV2 } from '@frontend/api-tag';
import { VoicemailApi, VoicemailTypes as VmTypes } from '@frontend/api-voicemails';
import { DashboardWidget, DashboardWidgetFC, useDashboardWidget } from '@frontend/grid-dashboard';
import { useTranslation } from '@frontend/i18n';
import { LocationFilterMenu } from '@frontend/location-filter-menu';
import { usePhoneConfigShallowStore } from '@frontend/phone-config';
import { useAppScopeStore, WeaveLocation } from '@frontend/scope';
import { useSlidePanelShallowStore } from '@frontend/slide-panel';
import { theme } from '@frontend/theme';
import {
  Chip,
  ContentLoader,
  emptyStateGraphics,
  styles,
  SwitchField,
  Text,
  useFormField,
} from '@frontend/design-system';
import { Action } from '../components/all-calls/shared-buttons';
import { MessageStatus, VoicemailQueryData } from '../components/all-calls/types';
import { TranscriptionContext } from '../components/voicemail-details/types';
import { useVoicemailActionsGetter, VoicemailActionsGetterProps } from '../components/voicemails/voicemail-actions';
import { VoicemailTableView } from '../components/voicemails/voicemail-table-view-components';
import { useVoicemailTagEvent, mutateVoicemailData } from '../components/voicemails/voicemail-update';
import { createVoicemailObject } from '../components/voicemails/voicemail-utils';
import { Forwardvoicemail, useForwardVoicemailModalControl } from '../components/voicemails/voicemails-forward';
import { getMailboxToNameMap, SetPageConfigInput } from '../components/voicemails/voicemails-multi.component';
import {
  AllCallsTable,
  usePhonePageSettingShallowStore,
  VoicemailFiltersType,
} from '../hooks/use-phone-config-state-store';
import { useVoicemailBoxList } from '../hooks/use-voicemail-box';
import { useVoicemailMutations } from '../hooks/use-voicemail-mutations';
import { queryKeys } from '../query-keys';
import { useVoicemailEventSubscription } from '../voicemail-event-emitter';
import useGetVoicemails from './hooks/use-get-voicemails';
import { SplitView } from './unread-voicemails';

/**
 * @dashboard-widget
 *
 * id: unread-voicemails-widget
 * title: Unread Voicemails
 * description: Listen and reply to unread voicemails from your dashboard.
 * icon: phone-small
 */

export const UnreadVoicemailsInfo: DashboardWidgetFC = () => {
  const { selectedLocationIds, selectedOrgId, accessibleLocationData } = useAppScopeStore();
  const queryClient = useQueryClient();
  const { currentSize } = useDashboardWidget();
  const { t } = useTranslation('calls', { keyPrefix: 'unread-voicemail-widget' });
  const { phoneConfig } = usePhoneConfigShallowStore('phoneConfig');
  const { title: defaultTitle } = useDashboardWidget();

  const locationOptions = useMemo(() => {
    return selectedLocationIds.map((id) => accessibleLocationData[id]);
  }, [selectedLocationIds, accessibleLocationData]);

  const [filteredLocationIds, setFilteredLocationIds] = useState(selectedLocationIds);

  const { panelType, context, setShow } = useSlidePanelShallowStore<TranscriptionContext>(
    'setShow',
    'context',
    'panelType'
  );

  const voicemailsUnreadCount = PhoneCallsQueries.useVoicemailsUnreadCount();

  const defaultFilters: VoicemailFiltersType = useMemo(
    () => ({
      locationIds: filteredLocationIds,
      syncDeviceSipProfileId: phoneConfig?.sipProfileId,
      filterOptions: {
        callerNumber: '',
        callerName: '',
        endTime: '',
        startTime: '',
        status: MessageStatus.MESSAGE_STATUS_UNREAD,
        mailboxIds: [],
        tagIds: [],
      },
    }),
    [filteredLocationIds, phoneConfig?.sipProfileId]
  );

  const {
    config,
    setPageNumber,
    setPageSize,
    setFilters: setVoicemailPageFilters,
  } = usePhonePageSettingShallowStore('config', 'setPageSize', 'setPageNumber', 'setFilters');

  const pageNumber = config[AllCallsTable.Unread_Voicemails].pageNumber;
  const pageSize = config[AllCallsTable.Unread_Voicemails].pageSize;
  const filters = config[AllCallsTable.Unread_Voicemails].filters ?? defaultFilters;

  const setPageConfig = useCallback(
    (input: SetPageConfigInput) => {
      const data = typeof input === 'function' ? input({ pageSize, pageNumber }) : input;
      if (data.pageSize) setPageSize(AllCallsTable.Unread_Voicemails, data.pageSize);
      if (data.pageNumber) setPageNumber(AllCallsTable.Unread_Voicemails, data.pageNumber);
    },
    [setPageSize, setPageNumber, pageSize, pageNumber]
  );

  const searchQuery = useMemo(
    () =>
      `${JSON.stringify({
        locationIds: filters.locationIds,
        sipProfileId: filters.syncDeviceSipProfileId,
      })}`,
    [filters.locationIds, filters.syncDeviceSipProfileId]
  );

  useEffect(() => {
    if (isEqual(filters.locationIds, filteredLocationIds)) {
      return;
    }
    setVoicemailPageFilters(AllCallsTable.Unread_Voicemails, { ...filters, locationIds: filteredLocationIds });
  }, [filteredLocationIds, filters, setVoicemailPageFilters]);

  const voicemailsQuery = useGetVoicemails(
    { searchQuery, filters, pageSize },
    {
      refetchOnWindowFocus: false,
      enabled: filteredLocationIds.length > 0,
    }
  );

  const { data: voicemailBoxList } = useVoicemailBoxList({
    phoneConfig,
    locationIds: selectedLocationIds,
  });

  const { fetchNextPage } = voicemailsQuery;

  // Helper functions for handling panel state after voicemail deletion
  const handleDeleteSuccess = useCallback(
    ({ voicemailId, mailboxId }: { voicemailId: string; mailboxId: string }) => {
      if (panelType === 'voicemailTranscription' && context?.currentRowId === voicemailId) {
        setShow(false);
      }

      // Update unread count in cache
      const totalUnreads = queryClient.getQueryData([
        selectedLocationIds,
        ...PhoneCallsQueries.queryKeys.unreadVoicemailCount(phoneConfig?.sipProfileId ?? ''),
      ]) as CountUnreadVoicemailsResponse;

      if (totalUnreads?.countPerMailbox) {
        // Find the mailbox ID from the deleted voicemail (this would need to be passed from the response)
        // For now, we'll just invalidate the unread count query to refetch it
        queryClient.invalidateQueries([
          selectedLocationIds,
          ...PhoneCallsQueries.queryKeys.unreadVoicemailCount(phoneConfig?.sipProfileId ?? ''),
        ]);
      }

      if (!totalUnreads?.countPerMailbox) {
        totalUnreads.countPerMailbox = {};
      }
      const currentUnreads = totalUnreads?.countPerMailbox?.[mailboxId ?? ''] ?? 0;
      if (currentUnreads > 0) {
        totalUnreads.countPerMailbox[mailboxId] = currentUnreads - 1;
      }
      queryClient.setQueryData(
        [selectedLocationIds, ...PhoneCallsQueries.queryKeys.unreadVoicemailCount(phoneConfig?.sipProfileId ?? '')],
        totalUnreads
      );
    },
    [panelType, context, setShow, queryClient, selectedLocationIds, phoneConfig?.sipProfileId]
  );

  // Use the new hook for cleaner mutation handling
  const { updateReadStatus, deleteVoicemail: onDelete } = useVoicemailMutations({
    selectedLocationIds,
    pageSize,
    pageNumber,
    queryKey: queryKeys.unreadVoicemails(searchQuery),
    fetchNextPage,
    onDeleteSuccess: handleDeleteSuccess,
  });

  const { forwardVoicemailModalData, openForwardVoicemailModal, forwardVoicemailModalProps } =
    useForwardVoicemailModalControl({
      voicemailBoxList: voicemailBoxList ?? [],
      onDelete,
    });

  const getVoicemailActions = useVoicemailActionsGetter({
    searchQuery,
    selectedLocationIds,
    openForwardVoicemailModal,
    updateReadStatus,
    deleteVoicemail: onDelete,
    trackingIdPrefix: 'unread-voicemails-widget',
  });

  const mailboxToNameMap = getMailboxToNameMap(voicemailBoxList ?? []);

  const { data: mappedTags = {} } = TagsV2.Queries.useListTagsQuery({
    request: { orgId: selectedOrgId, groupIds: selectedLocationIds },
    options: {
      select: (data) => {
        return data?.tags.reduce<Record<string, Tag>>((acc, tag) => {
          acc[tag.id] = tag;
          return acc;
        }, {});
      },
    },
  });

  const fullQueryKey = useMemo(
    () => [selectedLocationIds, ...queryKeys.unreadVoicemails(searchQuery)],
    [selectedLocationIds, searchQuery]
  );

  const getVoicemailData = useCallback(
    () => queryClient.getQueryData<InfiniteData<VoicemailQueryData>>(fullQueryKey),
    [queryClient, fullQueryKey]
  );

  const setVoicemailData = useCallback(
    (data: InfiniteData<VoicemailQueryData>) => queryClient.setQueryData(fullQueryKey, data),
    [queryClient, fullQueryKey]
  );

  const { handleVoicemailTagEvent } = useVoicemailTagEvent();

  const addNewVoicemail = (event: VoicemailUpdateEvent) =>
    queryClient
      .fetchQuery({
        queryKey: [selectedLocationIds, queryKeys.voicemailMessage(event.mailboxId, event.voicemailId)],
        queryFn: () => VoicemailApi.getVoicemail({ messageId: event.voicemailId }),
      })
      .then(({ voicemail }) => {
        if (voicemail) {
          const newData = mutateVoicemailData({
            bailIfNotFound: false,
            pageSize,
            voicemailData: getVoicemailData(),
            voicemailId: event.voicemailId,
            callbackFn: (data) => {
              if (data.voicemailRow) {
                data.voicemailRow.readAt = undefined;
              }

              const newVoicemail = createVoicemailObject(voicemail, selectedLocationIds, mappedTags, mailboxToNameMap);

              if (data.originalData.pages.length === 0) {
                data.originalData.pages.push({
                  rows: [],
                  meta: {
                    lastId: undefined,
                    createdAt: undefined,
                    hasNext: false,
                  },
                });
              }

              data.originalData.pages[0].rows.unshift(newVoicemail);
              data.originalData.pages[0].meta = {
                lastId: newVoicemail.voicemailId,
                createdAt: newVoicemail.createdAt,
                hasNext: false,
              };
            },
          });

          if (newData) {
            setVoicemailData(newData);
          }
        }
      });

  useVoicemailEventSubscription('voicemail:update', ({ event }) => {
    /**
     * TODO: These events could originate from the same client that triggered them - I could be on Client A, marking a voicemail as read, and then later receive
     * a websocket event for that very action, in Client A itself.
     *
     * We need a way to identify if the event originated from the same client, and if so, ignore it.
     */
    switch (event.type) {
      case VoicemailEventType.VOICEMAIL_EVENT_TYPE_NEW:
        addNewVoicemail(event);
        break;
      case VoicemailEventType.VOICEMAIL_EVENT_TYPE_READ: {
        // Just mark the voicemail as read, but do not remove it from the list.
        const newData = mutateVoicemailData({
          pageSize,
          voicemailData: getVoicemailData(),
          voicemailId: event.voicemailId,
          callbackFn: (data) => {
            if (data.voicemailRow) {
              data.voicemailRow.readAt = '0';
            }
          },
        });
        if (newData) {
          setVoicemailData(newData);
        }
        break;
      }
      case VoicemailEventType.VOICEMAIL_EVENT_TYPE_UNREAD: {
        let foundVoicemail = false;
        const newData = mutateVoicemailData({
          pageSize,
          voicemailData: getVoicemailData(),
          voicemailId: event.voicemailId,
          callbackFn: (data) => {
            if (data.voicemailRow) {
              data.voicemailRow.readAt = undefined;
              foundVoicemail = true;
            }
          },
        });

        if (newData) {
          setVoicemailData(newData);
        }

        if (!foundVoicemail) {
          addNewVoicemail(event);
        }

        break;
      }
      case VoicemailEventType.VOICEMAIL_EVENT_TYPE_DELETE: {
        const newData = mutateVoicemailData({
          pageSize,
          voicemailData: getVoicemailData(),
          voicemailId: event.voicemailId,
          callbackFn: (data) => {
            if (data.voicemailRow) {
              data.originalData.pages[0].rows.splice(data.voicemailIndexInPage, 1);
            }
          },
        });

        if (newData) {
          setVoicemailData(newData);
        }
        break;
      }
    }
  });

  useVoicemailEventSubscription('voicemail:tag', ({ event }) => handleVoicemailTagEvent(event));

  const isNarrow = currentSize === 'large-narrow';

  return (
    <DashboardWidget>
      <DashboardWidget.Header title={t('Phones')} />
      <DashboardWidget.Content
        css={{
          overflow: 'auto',
          display: 'grid',
          height: '100%',
          padding: theme.spacing(0, 1),
          gap: theme.spacing(1),
        }}
      >
        <UnreadVoicemailsContent
          title={defaultTitle}
          voicemailsQuery={voicemailsQuery}
          voicemailsUnreadCount={voicemailsUnreadCount}
          selectedLocationIds={selectedLocationIds}
          isNarrow={isNarrow}
          locationOptions={locationOptions}
          filteredLocationIds={filteredLocationIds}
          setFilteredLocationIds={setFilteredLocationIds}
          searchQuery={searchQuery}
          pageNumber={pageNumber}
          pageSize={pageSize}
          onDeleteVoicemail={onDelete}
          updateReadStatus={updateReadStatus}
          setPageConfig={setPageConfig}
          getVoicemailActions={getVoicemailActions}
        />
        <Forwardvoicemail modalProps={forwardVoicemailModalProps} forwardData={forwardVoicemailModalData} />
      </DashboardWidget.Content>
    </DashboardWidget>
  );
};

export const UnreadVoicemailsContent = ({
  title,
  voicemailsQuery,
  voicemailsUnreadCount,
  selectedLocationIds,
  isNarrow,
  locationOptions,
  filteredLocationIds,
  setFilteredLocationIds,
  searchQuery,
  pageSize,
  pageNumber,
  onDeleteVoicemail,
  updateReadStatus,
  setPageConfig,
  getVoicemailActions,
}: {
  title: string;
  voicemailsQuery: UseInfiniteQueryResult<VoicemailQueryData, unknown>;
  voicemailsUnreadCount: number;
  selectedLocationIds: string[];
  isNarrow: boolean;
  locationOptions: WeaveLocation[];
  filteredLocationIds: string[];
  setFilteredLocationIds: (ids: string[]) => void;
  searchQuery: string;
  pageNumber: number;
  pageSize: number;
  onDeleteVoicemail: (payload: VmTypes.DeletePayload) => void;
  updateReadStatus: (payload: VmTypes.StatusPayload) => void;
  setPageConfig: (input: SetPageConfigInput) => void;
  getVoicemailActions: (props: VoicemailActionsGetterProps) => Action[];
}) => {
  const { t } = useTranslation('calls', { keyPrefix: 'unread-voicemail-widget' });

  const switchProps = useFormField({
    type: 'switch',
    value: false,
  });

  const isDataAvailable = !voicemailsQuery.isLoading && (voicemailsQuery.data?.pages?.[0]?.rows?.length || 0) > 0;

  return (
    <>
      <section
        css={{
          display: 'grid',
          gridTemplateColumns: 'auto auto',
          alignItems: 'center',
          gap: theme.spacing(2),
          minWidth: 0,
        }}
      >
        <div
          css={{
            display: 'flex',
            gap: theme.spacing(1),
            alignItems: 'center',
            minWidth: 0,
          }}
        >
          <Text as='h3' weight='bold'>
            {title}
          </Text>
          {isDataAvailable && !!voicemailsUnreadCount && <Chip variant='primary'>{voicemailsUnreadCount}</Chip>}
        </div>

        <div
          css={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: theme.spacing(2),
            justifySelf: 'end',
            minWidth: 0,
          }}
        >
          {isDataAvailable && !isNarrow && (
            <SwitchField
              {...switchProps}
              css={{ alignItems: 'center' }}
              name='toggleView'
              label={t('Table View')}
              data-trackingid='unread-voicemails-widget-toggle-btn'
            />
          )}

          {selectedLocationIds.length > 1 && (
            <LocationFilterMenu
              locations={locationOptions}
              selectedOptions={selectedLocationIds}
              onChange={setFilteredLocationIds}
              showClearAll={false}
              css={{ maxWidth: 200 }}
            />
          )}
        </div>
      </section>
      <section
        css={{
          minWidth: 0,
        }}
      >
        <ContentLoader show={voicemailsQuery.isLoading} />

        {!isDataAvailable && (
          <div
            css={[
              styles.flexCenter,
              { flexDirection: 'column', flexGrow: 1, gap: theme.spacing(2), paddingTop: theme.spacing(2) },
            ]}
          >
            {!!filteredLocationIds.length && (
              <div
                css={{
                  display: 'flex',
                  alignItems: 'center',
                  flexDirection: 'column',
                }}
              >
                <emptyStateGraphics.voicemail height={150} width={150} />

                <Text
                  css={{
                    marginTop: theme.spacing(3),
                    fontSize: theme.fontSize(20),
                    maxWidth: 400,
                  }}
                  textAlign='center'
                  weight='bold'
                  color='subdued'
                >
                  {t('No Items to Display')}
                </Text>
                <Text
                  css={{
                    marginTop: theme.spacing(1),
                    fontSize: theme.fontSize(16),
                    maxWidth: 400,
                  }}
                  color='subdued'
                >
                  {t('Voicemails will display here.')}
                </Text>
              </div>
            )}

            {!filteredLocationIds.length && <Text color='light'>{t('No locations selected')}</Text>}
          </div>
        )}
        {isDataAvailable && !isNarrow && (
          <div
            css={{
              height: '100%',
              overflow: 'auto',
            }}
          >
            {!switchProps.value ? (
              <SplitView
                voicemailDataQuery={voicemailsQuery}
                queryKey={queryKeys.unreadVoicemails(searchQuery).filter(Boolean)}
                onDeleteVoicemail={onDeleteVoicemail}
                updateReadStatus={updateReadStatus}
              />
            ) : (
              <VoicemailTableView
                showVmTranscriptionButton={false}
                fullHeightConfig={{
                  minHeight: 230,
                  maxHeight: 290,
                }}
                pageNumber={pageNumber}
                showNumberRowSelector={false}
                columnIds={['contactName', 'voicemailMessage', 'dateTime', 'tags', 'actions']}
                isLoading={voicemailsQuery.isLoading}
                pageSize={pageSize}
                infiniteQueryProps={voicemailsQuery}
                panelProps={{
                  deleteVoicemail: onDeleteVoicemail,
                  updateReadStatus,
                  sectionFocus: 'transcript',
                  voicemailsQueryKey: queryKeys.unreadVoicemails(searchQuery),
                }}
                searchQuery={searchQuery}
                selectedLocationIds={selectedLocationIds}
                setPageConfig={setPageConfig}
                getVoicemailActions={getVoicemailActions}
                styleConfig={{
                  columns: [
                    {
                      id: '*',
                      headerStyler: css`
                        border-right: none !important;
                        border-left: none !important;
                      `,
                    },
                  ],
                }}
              />
            )}
          </div>
        )}
        {isDataAvailable && isNarrow && (
          <VoicemailTableView
            showVmTranscriptionButton={false}
            fullHeightConfig={{
              minHeight: 230,
              maxHeight: 290,
            }}
            pageNumber={pageNumber}
            showNumberRowSelector={false}
            columnIds={['contactName', 'voicemailMessage', 'dateTime', 'tags', 'actions']}
            isLoading={voicemailsQuery.isLoading}
            pageSize={pageSize}
            infiniteQueryProps={voicemailsQuery}
            panelProps={{
              deleteVoicemail: onDeleteVoicemail,
              updateReadStatus,
              sectionFocus: 'transcript',
              voicemailsQueryKey: queryKeys.unreadVoicemails(searchQuery),
            }}
            searchQuery={searchQuery}
            selectedLocationIds={selectedLocationIds}
            setPageConfig={setPageConfig}
            getVoicemailActions={getVoicemailActions}
            styleConfig={{
              columns: [
                {
                  id: '*',
                  headerStyler: css`
                    border-right: none !important;
                    border-left: none !important;
                  `,
                },
              ],
            }}
          />
        )}
      </section>
    </>
  );
};

UnreadVoicemailsInfo.config = {
  size: {
    large: 'large-wide',
    medium: 'large-wide',
    small: 'large-narrow',
    extraSmall: 'large-narrow',
  },
  feature: 'phone',
};
